import React, { useCallback } from 'react';
import { Box } from 'lucide-react';
import type { GeneratorPadSection } from '@officehub/shared';
import { INPUT_CLASS, LABEL_CLASS, TEXTAREA_CLASS, TOGGLE_ACTIVE, TOGGLE_INACTIVE } from './styles';
import type { RenderSectionPhotoCapture } from './photoCapture';

export interface GeneratorPadFormProps {
  data: GeneratorPadSection;
  onUpdate: (d: Partial<GeneratorPadSection>) => void;
  /**
   * Optional host-supplied photo capture panel. When omitted the Photos block
   * is not rendered (structured-data-only capture).
   */
  renderPhotoCapture?: RenderSectionPhotoCapture;
}

const TOGGLE_BASE = 'flex-1 py-2.5 text-xs rounded-xl transition-all duration-150 min-h-[48px]';

/** A typed measurement, or null (not recorded) for empty, whitespace-only or non-numeric input: never 0. */
export function parseMeasurement(value: string): number | null {
  if (value.trim() === '') return null; // Number('  ') is 0
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function GeneratorPadForm({
  data,
  onUpdate,
  renderPhotoCapture,
}: GeneratorPadFormProps): React.ReactElement {
  // Pad dimensions and clearances clear to null (not recorded), never 0: the report prints a stored 0 as a measurement.
  const handleChange = useCallback(
    (field: keyof GeneratorPadSection, value: unknown) => {
      onUpdate({ [field]: value });
    },
    [onUpdate],
  );

  const handleClearanceChange = useCallback(
    (side: 'front' | 'back' | 'left' | 'right', value: string) => {
      // An untouched or cleared side is null (not recorded), never 0: a stored 0 reads as a measured "0 ft".
      const num = parseMeasurement(value);
      onUpdate({
        clearances: {
          front: data.clearances?.front ?? null,
          back: data.clearances?.back ?? null,
          left: data.clearances?.left ?? null,
          right: data.clearances?.right ?? null,
          [side]: num,
        },
      });
    },
    [onUpdate, data.clearances],
  );

  const handleDimensionChange = useCallback(
    (dim: 'length' | 'width', value: string) => {
      const num = parseMeasurement(value);
      onUpdate({
        padDimensions: {
          length: data.padDimensions?.length ?? null,
          width: data.padDimensions?.width ?? null,
          [dim]: num,
        },
      });
    },
    [onUpdate, data.padDimensions],
  );

  return (
    <div className="flex flex-col gap-6">
      {/* Section header */}
      <div className="flex items-center gap-2 px-1">
        <Box className="w-3.5 h-3.5 text-slate-400" />
        <h2 className="text-[11px] font-black text-slate-500 uppercase tracking-[0.1em]">
          Generator Pad
        </h2>
        <div className="flex-1 h-px bg-gradient-to-r from-slate-200 to-transparent" />
      </div>

      {/* Proposed Location — full width */}
      <div>
        <label className={LABEL_CLASS}>Proposed Location</label>
        <input
          type="text"
          className={INPUT_CLASS}
          value={data.proposedLocation ?? ''}
          onChange={(e) => handleChange('proposedLocation', e.target.value)}
          placeholder="e.g., Left side yard, 5 ft from house"
        />
      </div>

      {/* Pad Exists toggle */}
      <div>
        <label className={LABEL_CLASS}>Pad Exists</label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => handleChange('padExists', true)}
            className={`${TOGGLE_BASE} ${data.padExists ? TOGGLE_ACTIVE : TOGGLE_INACTIVE}`}
          >
            Yes
          </button>
          <button
            type="button"
            onClick={() => handleChange('padExists', false)}
            className={`${TOGGLE_BASE} ${!data.padExists ? TOGGLE_ACTIVE : TOGGLE_INACTIVE}`}
          >
            No
          </button>
        </div>
      </div>

      {/* Pad Dimensions */}
      <div>
        <label className={LABEL_CLASS}>Pad Dimensions (ft)</label>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] font-bold text-slate-500 mb-1">Length</label>
            <input
              type="number"
              inputMode="decimal"
              className={INPUT_CLASS}
              value={data.padDimensions?.length ?? ''}
              onChange={(e) => handleDimensionChange('length', e.target.value)}
              placeholder="e.g., 48"
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-slate-500 mb-1">Width</label>
            <input
              type="number"
              inputMode="decimal"
              className={INPUT_CLASS}
              value={data.padDimensions?.width ?? ''}
              onChange={(e) => handleDimensionChange('width', e.target.value)}
              placeholder="e.g., 30"
            />
          </div>
        </div>
      </div>

      {/* Crane Access toggle */}
      <div>
        <label className={LABEL_CLASS}>Crane Access</label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => handleChange('craneAccess', true)}
            className={`${TOGGLE_BASE} ${data.craneAccess ? TOGGLE_ACTIVE : TOGGLE_INACTIVE}`}
          >
            Yes
          </button>
          <button
            type="button"
            onClick={() => handleChange('craneAccess', false)}
            className={`${TOGGLE_BASE} ${!data.craneAccess ? TOGGLE_ACTIVE : TOGGLE_INACTIVE}`}
          >
            No
          </button>
        </div>
      </div>

      {/* Crane Access Notes — shown only when craneAccess is true */}
      {data.craneAccess && (
        <div>
          <label className={LABEL_CLASS}>Crane Access Notes</label>
          <input
            type="text"
            className={INPUT_CLASS}
            value={data.craneAccessNotes ?? ''}
            onChange={(e) => handleChange('craneAccessNotes', e.target.value)}
            placeholder="Street access, overhead obstructions, weight limits…"
          />
        </div>
      )}

      {/* Clearances */}
      <div>
        <label className={LABEL_CLASS}>Clearances (ft)</label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(['front', 'back', 'left', 'right'] as const).map((side) => (
            <div key={side}>
              <label className="block text-[10px] font-bold text-slate-500 capitalize mb-1">
                {side}
              </label>
              <input
                type="number"
                inputMode="decimal"
                className={INPUT_CLASS}
                value={data.clearances?.[side] ?? ''}
                onChange={(e) => handleClearanceChange(side, e.target.value)}
                placeholder="ft"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Exhaust Routing Notes — full width */}
      <div>
        <label className={LABEL_CLASS}>Exhaust Routing Notes</label>
        <textarea
          className={TEXTAREA_CLASS}
          rows={3}
          value={data.exhaustRoutingNotes ?? ''}
          onChange={(e) => handleChange('exhaustRoutingNotes', e.target.value)}
          placeholder="Exhaust direction, clearances from windows/doors, routing path…"
        />
      </div>

      {/* Photos — only when the host supplies a capture panel */}
      {renderPhotoCapture && (
        <div>
          <div className="flex items-center gap-2 mb-3 px-1">
            <h3 className="text-[11px] font-black text-slate-500 uppercase tracking-[0.1em]">Photos</h3>
            <div className="flex-1 h-px bg-gradient-to-r from-slate-200 to-transparent" />
          </div>
          {renderPhotoCapture({
            section: 'generatorPad',
            photos: data.photos,
            availableTags: [
              { value: 'proposed_pad', label: 'Proposed Pad' },
              { value: 'site_overview', label: 'Site Overview' },
            ],
          })}
        </div>
      )}
    </div>
  );
}
